import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GetServerSideProps } from 'next';
import { sitecoreClient, withOps } from '@src/utils/graphql-request';
import {
    CategoriesFragment,
    DiscoverMainContentFolderFragment,
    HashtagsFragment,
    HashtagsQueryResult,
    KolSectionItemFragment,
    MainContentBlockQueryResult,
} from '@graphqlTypes/sitecore';
import BigCarousel from '@components/big-carousel/BigCarousel';
import RowOfTwo from '@components/row-of-two/RowOfTwo';
import BigSingle from '@components/big-single/BigSingle';
import TwoInAViewCarousel from '@components/two-in-a-view-carousel/TwoInAViewCarousel';
import TopBanner from '@components/top-banner/TopBanner';
import { AppLanguage } from '@src/common/app-language';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import cn from 'classnames';
import { contentArr } from '@src/utils/util';
import { observer } from 'mobx-react-lite';
import { useGlobalStore } from '@src/hooks/use-global-store';
import { sortMainContentsByScores } from '@src/common/scores/sort-contents';
import { Resource } from 'i18next';
import HashTagList from '@components/hashTagList';
import { v4 as uuidv4 } from 'uuid';
import { TagContentBlockFragment } from '@src/common/types';
import { filterContentBlocksInAllSections } from '@src/common/filter';
import useWindowSize from '@src/utils/useWindowSize';
import Open3in1App from '@components/open-3in1-app/Open3in1App';
import isSharePageStore from '@mobx/stores/isShare-store';
import VerticalThumbnail from '@components/vertical-thumbnail/vertical-thumnail';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const components: any = {
    ['big-carousel']: BigCarousel,
    ['row-of-two']: RowOfTwo,
    ['big-single']: BigSingle,
    ['two-in-a-view-carousel']: TwoInAViewCarousel,
    ['vertical-thumbnail']: VerticalThumbnail,
};

type HomeProps = {
    mainContent: MainContentBlockQueryResult;
    hashTags: HashtagsQueryResult;
    kolRankData: KolSectionItemFragment | null;
    categoryData: CategoriesFragment[];
    _nextI18Next: { initialI18nStore: Resource; initialLocale: string };
};

function Home({ mainContent, hashTags, kolRankData, categoryData }: HomeProps) {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const tagListRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation(['common', 'home', 'share']);
    const globalStore = useGlobalStore();
    const global = globalStore.baseStore;
    const uId = uuidv4();
    const { width, height } = useWindowSize();
    const isWidth1440 = width >= 1440;

    const [select, setSelect] = useState('all');
    const [tagTitleName, setTagTitleName] = useState('');
    const [tagsList, setTagsList] = useState([] as TagContentBlockFragment[]);
    const [showS, setShowS] = useState(false);
    const [pBNum, setPBNum] = useState(0);
    const heroBannerSection = mainContent.Item?.sections?.find(
        (section) => section.key === 'hero-banner'
    );
    const noHeroBannerSection = mainContent.Item?.sections?.filter(
        (section) => section.key !== 'hero-banner'
    );
    function filterData(
        originalData: DiscoverMainContentFolderFragment[] | undefined,
        compareData: HashtagsFragment[] | undefined
    ) {
        const allList: string[] = [];
        originalData?.forEach((i) => {
            i.contentBlocks.length > 0 &&
                i.contentBlocks.forEach((n) => {
                    n.hashTags?.tags &&
                        n.hashTags?.tags?.length > 0 &&
                        n.hashTags?.tags?.forEach((c) => {
                            allList.push(c.name);
                        });
                });
        });
        return (compareData || []).filter((item: HashtagsFragment) => allList.includes(item.key));
    }
    const result = filterData(mainContent?.Item?.sections, hashTags?.Item?.data) || [];
    const resultObject = { Item: { data: result } };
    const hashData: HashtagsFragment[] = [];
    resultObject.Item.data.map((i) => {
        if (i?.isDisplay?.boolValue) {
            hashData.push(i);
        }
    });

    const tagsListFun = (tag: string) => {
        const filterData = noHeroBannerSection?.flatMap((section: any) =>
            section.contentBlocks.length > 0
                ? section.contentBlocks
                      .filter(
                          (block: any) =>
                              block.hashTags &&
                              block.hashTags.tags &&
                              block.hashTags.tags.length > 0 &&
                              block.hashTags.tags.some((h: any) => h.name === tag)
                      )
                      .map((contentBlock: any) => {
                          let matchingTagName = '';
                          matchingTagName =
                              contentBlock?.hashTags?.tags?.find((h: any) => h.name === tag)
                                  ?.tagName?.value ?? '';

                          return {
                              ...contentBlock,
                              currentPath: section.key,
                              tagName: matchingTagName,
                          };
                      })
                : []
        );
        setTagsList(filterData ?? []);
        setShowS(true);
    };

    const initData = useCallback(() => {
        sessionStorage.setItem('hasTagSortingKey', 'Relevance');
        isSharePageStore.setIsSharePage(true);
        sessionStorage.setItem('selectedFilter', 'all');
        global.indexSLTagScroll = 0;
    }, [global]);

    useEffect(() => {
        initData();
    }, [initData]);

    useEffect(() => {
        if (globalStore.baseStore.data?.contentScoringConfig) {
            sortMainContentsByScores(
                mainContent,
                globalStore.shareDataStore.categoryScores,
                globalStore.shareDataStore.hashtagScores,
                globalStore.baseStore.data.contentScoringConfig
            );
        }
    }, [
        globalStore.baseStore.data?.contentScoringConfig,
        globalStore.shareDataStore.categoryScores,
        globalStore.shareDataStore.hashtagScores,
        mainContent,
    ]);

    useEffect(() => {
        const sessTagName = sessionStorage.getItem('hashTagName') ?? '';
        if (sessTagName && sessTagName !== 'all') {
            tagsListFun(sessTagName);
            setTagTitleName(sessionStorage.getItem('hashTagTitle') ?? '');
        } else {
            setSelect('all');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={cn(
                'bg-[#E0E0E0] overflow-x-hidden',
                isWidth1440 && 'gird place-items-center'
            )}
        >
            <div
                className={cn('pb-[24px] bg-[#F0F0F0]')}
                style={{
                    width: isWidth1440 ? '1440px' : '100%',
                    maxWidth: '1440px',
                    margin: '0 auto',
                    minHeight: `${height}px`,
                    paddingBottom: `${pBNum + 20}px`,
                }}
                ref={contentRef}
            >
                {/* hashTag Nav*/}
                <div
                    className={`bg-[#D2DAE8] ${
                        select === 'all' ? 'sticky' : 'fixed'
                    } w-full top-0 z-30 h-[54px] flex overflow-y-hidden overflow-x-auto flex-nowrap whitespace-nowrap pt-[12px] pl-[15px] pr-[15px] no-scrollbar text-[15px]`}
                    style={{
                        maxWidth: '1440px',
                        margin: '0 auto',
                    }}
                >
                    <div
                        onClick={() => {
                            initData();
                            globalStore.baseStore.indexHasTag = 'all';
                            globalStore.baseStore.indexHasTagScroll = 0;
                            setSelect('all');
                            sessionStorage.setItem('hashTagName', 'all');
                        }}
                        className={cn(
                            `text-center h-[30px] leading-[30px] rounded-full px-3  bg-[#122C68] mr-[8px]
                                ${
                                    select === 'all'
                                        ? 'bg-[#122C68] text-white'
                                        : 'bg-white text-[#122C68]'
                                }
                                `
                        )}
                    >
                        {t('common:textLabel')}
                    </div>

                    {hashData?.map((i, index) => {
                        return (
                            <div
                                className={cn(
                                    `text-center h-[30px] leading-[30px] rounded-full px-[15px] mr-2 text-[#122C68] ${
                                        i?.key === select ? 'bg-[#122C68] text-white' : 'bg-white'
                                    } `
                                )}
                                key={`${uId}${index}`}
                                dangerouslySetInnerHTML={{
                                    __html: i?.tagName?.value || '',
                                }}
                                onClick={async () => {
                                    await setShowS(false);
                                    globalStore.baseStore.indexHasTag = i.key;

                                    i?.key !== select
                                        ? (sessionStorage.setItem('hashTagName', i?.key),
                                          sessionStorage.setItem(
                                              'hashTagTitle',
                                              i?.tagName?.value ?? ''
                                          ))
                                        : (sessionStorage.removeItem('hashTagName'),
                                          sessionStorage.removeItem('hashTagTitle'));

                                    // reset hasTagSortingKey to Relevance when tag switch
                                    sessionStorage.setItem('hasTagSortingKey', 'Relevance');
                                    setSelect((i?.key !== select && i?.key) || 'all');
                                    setTagTitleName(i?.tagName?.value ?? '');
                                    await tagsListFun(i?.key);
                                }}
                            ></div>
                        );
                    })}
                </div>

                {select === 'all' &&
                mainContent &&
                mainContent.Item?.sections &&
                mainContent.Item?.sections.length > 0 ? (
                    <>
                        {heroBannerSection && (
                            <TopBanner content={heroBannerSection} filterValue={select} />
                        )}
                        {/* main content start */}
                        {mainContent?.Item &&
                            mainContent?.Item?.sections.slice(1).map((i: any, index: number) => {
                                const layoutName = i?.layoutType?.layout?.key || 'big-carousel';
                                const Tag = components[layoutName];
                                const newArr = contentArr(select, i);
                                return (
                                    Tag &&
                                    newArr.length > 0 &&
                                    select === 'all' && (
                                        <Tag
                                            content={i}
                                            filterValue={select}
                                            key={`${uId}${index}`}
                                            kolRankData={kolRankData}
                                            categoryData={categoryData}
                                        ></Tag>
                                    )
                                );
                            })}
                    </>
                ) : (
                    <div ref={tagListRef}>
                        {
                            // tagList content
                            tagsList.length > 0 && select !== 'all' && showS && (
                                <HashTagList hashTagsList={tagsList} tagTitleName={tagTitleName} />
                            )
                        }
                    </div>
                )}
            </div>

            {select === 'all' && (
                <Open3in1App currentPath={''} getOverLayHeight={(v) => setPBNum(v)} />
            )}
        </div>
    );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
    const [hashTags, contentFolderName, kolLeaderItem, CategoryItem] = await Promise.all([
        withOps('sharehome-Hashtag', () =>
            sitecoreClient.HashtagsQuery({ lang: ctx.locale || '' })
        ),
        withOps('sharehome-contentFolderName', () =>
            sitecoreClient.MainContentFolderNameQuery({ lang: ctx.locale || '' })
        ),
        withOps('sharehome-kolLeaderItem', () =>
            sitecoreClient.getKolSectionPageData({ lang: ctx.locale || '' })
        ),
        withOps('sharehome-CategoryItem', () => sitecoreClient.CategoriesQuery({ lang: 'zh-CN' })),
    ]);

    const mainContent = await (async () => {
        try {
            const promises = contentFolderName.Item?.folderName.map(async (section) => {
                const path = `/sitecore/content/Sites/Discover/MainContent/${section.key}`;
                const response = await withOps(`sharehome-${section.key}`, () =>
                    sitecoreClient.HomeContentQuery({
                        path,
                        lang: ctx.locale || '',
                    })
                );
                return response.Item;
            });

            const results = (await Promise.all(promises || [])).filter(
                (item): item is DiscoverMainContentFolderFragment => item !== undefined
            ) as DiscoverMainContentFolderFragment[];

            const newData = {
                Item: {
                    sections: results,
                },
            };
            return newData;
        } catch (error) {
            console.error('Error fetching sections:', error);
            throw error;
        }
    })();

    filterContentBlocksInAllSections(mainContent, ctx.query.seg, ctx.query.ac);
    /** 获取kolLeader rank数据 */
    const kolLeaderList = kolLeaderItem.Item?.kolLeaderItem || [];
    const kolRankData = kolLeaderList.filter((i) => i.template?.name === 'PageKolLeaderboard');
    /** 简体中文category data */
    const categoryData = CategoryItem?.Item?.data ?? [];

    return {
        props: {
            mainContent,
            hashTags,
            kolRankData: kolRankData.length > 0 ? kolRankData[0] : null,
            categoryData,
            ...(await serverSideTranslations(ctx.locale || AppLanguage.en_us, [
                'common',
                'home',
                'share',
            ])),
        },
    };
};

export default observer(Home);
